import { FOOD, THIRD_PARTY, TUDO_SOCIAL } from "./modules";
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Briefcase,
  Network,
  Globe,
  ClipboardList,
  ClipboardCheck,
  Headset,
  MessageSquare,
  FileText,
  BarChart3,
  TrendingUp,
  Activity,
  PieChart,
  Gauge,
  Megaphone,
  ServerCog,
  Bell,
  Shield,
  ShieldAlert,
  FileDown,
  Wallet,
  ArrowLeftRight,
  Landmark,
  Receipt,
  CreditCard,
  Plane,
  BookOpen,
  Lightbulb,
  Clapperboard,
  Dice5,
  Film,
  FileCheck,
  Sparkles,
  UtensilsCrossed,
  Truck,
  Package,
  Tag,
  History,
  MapPin,
  DollarSign,
  Building2,
  Gift,
  Star,
  ListPlus,
  LayoutGrid,
  Inbox,
  ShoppingBag,
  ShoppingCart,
  MessageCircle,
  ToggleRight,
  DoorOpen,
  Car,
  Store,
  Handshake,
  LifeBuoy,
  PanelsTopLeft,
  Coins,
  KeyRound,
  Settings,
  Map,
  Layers,
  type LucideIcon,
} from "lucide-react";

export interface SidebarMenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  /** Permission codename required to see this item. Null = always visible. */
  permission: string | null;
  /**
   * Optional sub-heading inside the group (e.g. "Operations" / "Setup").
   * Items sharing a subgroup must be contiguous in the group's list.
   */
  subgroup?: string;
}

export interface SidebarMenuGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  /**
   * Groups defined with a single item render as a direct link (using the
   * group's label + icon) instead of an accordion.
   */
  items: SidebarMenuItem[];
}

export interface SidebarMenuSection {
  id: string;
  /** Section heading. Omitted for the top-level Dashboard section. */
  label?: string;
  groups: SidebarMenuGroup[];
}

// Shared subgroup names so the module groups read the same way.
const OPS = "Operations";
const SETUP = "Setup";

// Tudo Social is an umbrella over several products, so its group is split by
// SURFACE rather than by Operations/Setup like the single-product modules.
// Flat, the group reads as eight unrelated screens and nothing tells you that
// "Articles" and "Channels" belong to different products.
const PEDIA = "Tudopedia";
const VERSE = "TudoVerse";
const TUDOTV = "TudoTV";
const GAMES = "Games";

/**
 * Sidebar navigation — section → group → items (with optional subgroups).
 * Items are filtered at render-time based on the user's RBAC permissions;
 * groups and sections with no visible items are hidden.
 */
export const sidebarMenuSections: SidebarMenuSection[] = [
  {
    id: "overview",
    groups: [
      {
        id: "overview",
        label: "Dashboard",
        icon: LayoutDashboard,
        items: [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: LayoutDashboard,
            path: "/dashboard",
            permission: "dashboard.view",
          },
        ],
      },
    ],
  },

  // ─── Operations ──────────────────────────────────────────────────────────
  {
    id: "operations",
    label: "Operations",
    groups: [
      {
        id: "approvals",
        label: "Approvals",
        icon: ClipboardCheck,
        items: [
          {
            id: "providers-approval",
            label: "Provider Approvals",
            icon: UserCheck,
            path: "/providers/approval",
            permission: "provider.view",
          },
          {
            id: "companies-approval",
            label: "Company Approvals",
            icon: Building2,
            path: "/companies/approval",
            permission: "company.view",
          },
          {
            id: "services-approval",
            label: "Service Approvals",
            icon: Briefcase,
            path: "/services/approval",
            permission: "service.view",
          },
        ],
      },
      {
        id: "accounts",
        label: "Users & Partners",
        icon: Users,
        items: [
          {
            id: "users",
            label: "Users",
            icon: Users,
            path: "/users",
            permission: "user.view",
          },
          {
            id: "companies-list",
            label: "Companies",
            icon: Building2,
            path: "/companies",
            permission: "company.view",
          },
          {
            id: "franchise",
            label: "Franchise",
            icon: Network,
            path: "/franchise",
            permission: "franchise.view",
          },
        ],
      },
    ],
  },

  // ─── Modules (one group per vertical) ───────────────────────────────────
  {
    id: "modules",
    label: "Modules",
    groups: [
      {
        id: "professional-services",
        label: "Professional Services",
        icon: Briefcase,
        items: [
          {
            id: "service-listings",
            label: "Service Listings",
            icon: ClipboardList,
            path: "/service-listings",
            permission: "service.view",
            subgroup: OPS,
          },
          {
            id: "service-type-requests",
            label: "Service Requests",
            icon: ListPlus,
            path: "/service-type-requests",
            permission: "service.manage",
            subgroup: OPS,
          },
          {
            id: "service-promo-codes",
            label: "Promo Codes",
            icon: Tag,
            path: "/service-promo-codes",
            permission: "service.manage",
            subgroup: OPS,
          },
          {
            // Regional (per-country / per-city) service rules. The global
            // catalog lives under Platform Setup → Global Catalog.
            id: "service-catalog",
            label: "Regional Catalog",
            icon: Briefcase,
            path: "/service-management",
            permission: "service.manage",
            subgroup: SETUP,
          },
          {
            id: "service-onboarding",
            label: "Onboarding",
            icon: DoorOpen,
            path: "/service-management/onboarding",
            permission: "service.manage",
            subgroup: SETUP,
          },
          {
            id: "service-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/service-management/customer-availability",
            permission: "service.manage",
            subgroup: SETUP,
          },
        ],
      },
      {
        id: "food-delivery",
        label: "Food Delivery",
        icon: UtensilsCrossed,
        items: [
          {
            id: "food-dashboard",
            label: "Dashboard",
            icon: BarChart3,
            path: "/food/dashboard",
            permission: FOOD.can("dashboard", "view"),
            subgroup: OPS,
          },
          {
            id: "food-restaurants",
            label: "Restaurants",
            icon: UtensilsCrossed,
            path: "/food/restaurants",
            permission: FOOD.can("restaurant", "view"),
            subgroup: OPS,
          },
          {
            id: "food-orders",
            label: "Orders",
            icon: Package,
            path: "/food/orders",
            permission: FOOD.can("order", "view"),
            subgroup: OPS,
          },
          {
            id: "food-riders",
            label: "Riders",
            icon: Truck,
            path: "/food/riders",
            permission: FOOD.can("rider", "view"),
            subgroup: OPS,
          },
          {
            id: "food-deals",
            label: "Deals",
            icon: Tag,
            path: "/food/deals",
            permission: FOOD.can("deal", "view"),
            subgroup: OPS,
          },
          {
            id: "food-country-catalog",
            label: "Regional Catalog",
            icon: UtensilsCrossed,
            path: "/food/country-catalog",
            permission: FOOD.can("country_rule", "edit"),
            subgroup: SETUP,
          },
          {
            id: "food-delivery-fee",
            label: "Delivery Fee",
            icon: Receipt,
            path: "/food/delivery-fee",
            permission: FOOD.can("delivery_fee", "view"),
            subgroup: SETUP,
          },
          {
            id: "food-onboarding",
            label: "Onboarding",
            icon: DoorOpen,
            path: "/food/onboarding",
            permission: FOOD.can("country_rule", "view"),
            subgroup: SETUP,
          },
          {
            id: "food-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/food/customer-availability",
            permission: FOOD.can("country_rule", "view"),
            subgroup: SETUP,
          },
        ],
      },
      {
        id: "grocery",
        label: "Grocery",
        icon: ShoppingCart,
        items: [
          {
            id: "grocery-dashboard",
            label: "Dashboard",
            icon: LayoutDashboard,
            path: "/grocery/dashboard",
            permission: "grocery.view",
            subgroup: OPS,
          },
          {
            id: "grocery-store-approvals",
            label: "Store Approvals",
            icon: Package,
            path: "/grocery/stores",
            permission: "grocery.view",
            subgroup: OPS,
          },
          {
            id: "grocery-products",
            label: "Product Listings",
            icon: Tag,
            path: "/grocery/products",
            permission: "grocery.view",
            subgroup: OPS,
          },
          {
            id: "grocery-orders",
            label: "Orders",
            icon: ClipboardList,
            path: "/grocery/orders",
            permission: "grocery.view",
            subgroup: OPS,
          },
          {
            id: "grocery-reviews",
            label: "Reviews",
            icon: Star,
            path: "/grocery/reviews",
            permission: "grocery.view",
            subgroup: OPS,
          },
          {
            id: "grocery-catalog",
            label: "Catalog",
            icon: ShoppingBag,
            path: "/grocery/catalog",
            permission: "grocery.manage_catalog",
            subgroup: SETUP,
          },
          {
            id: "grocery-onboarding",
            label: "Onboarding",
            icon: DoorOpen,
            path: "/grocery/onboarding",
            permission: "grocery.view",
            subgroup: SETUP,
          },
          {
            id: "grocery-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/grocery/customer-availability",
            permission: "grocery.view",
            subgroup: SETUP,
          },
        ],
      },
      {
        id: "ecommerce",
        label: "Ecommerce",
        icon: Store,
        items: [
          {
            id: "ecommerce-dashboard",
            label: "Dashboard",
            icon: LayoutDashboard,
            path: "/ecommerce/dashboard",
            permission: "ecommerce.view",
            subgroup: OPS,
          },
          {
            id: "ecommerce-store-approvals",
            label: "Store Approvals",
            icon: ShoppingBag,
            path: "/ecommerce/stores",
            permission: "ecommerce.view",
            subgroup: OPS,
          },
          {
            id: "ecommerce-products",
            label: "Product Listings",
            icon: Package,
            path: "/ecommerce/products",
            permission: "ecommerce.view",
            subgroup: OPS,
          },
          {
            id: "ecommerce-orders",
            label: "Orders",
            icon: ClipboardList,
            path: "/ecommerce/orders",
            permission: "ecommerce.view",
            subgroup: OPS,
          },
          {
            id: "ecommerce-disputes",
            label: "Disputes",
            icon: ShieldAlert,
            path: "/ecommerce/disputes",
            permission: "ecommerce.view",
            subgroup: OPS,
          },
          {
            id: "ecommerce-reviews",
            label: "Reviews",
            icon: Star,
            path: "/ecommerce/reviews",
            permission: "ecommerce.view",
            subgroup: OPS,
          },
          {
            id: "ecommerce-catalog",
            label: "Catalog",
            icon: Tag,
            path: "/ecommerce/catalog",
            permission: "ecommerce.manage_catalog",
            subgroup: SETUP,
          },
          {
            id: "ecommerce-onboarding",
            label: "Onboarding",
            icon: DoorOpen,
            path: "/ecommerce/onboarding",
            permission: "ecommerce.view",
            subgroup: SETUP,
          },
          {
            id: "ecommerce-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/ecommerce/customer-availability",
            permission: "ecommerce.view",
            subgroup: SETUP,
          },
        ],
      },
      {
        // The `rideHailing` backend app. The legacy `ride` app's section was
        // removed from the admin UI; its backend + `ride.*` perms are untouched.
        id: "ride-hailing",
        label: "Ride Hailing",
        icon: Car,
        items: [
          {
            id: "ride-hailing-trips",
            label: "Trips",
            icon: MapPin,
            path: "/ride-hailing/trips",
            permission: "ride_hailing.view_trips",
            subgroup: OPS,
          },
          {
            id: "ride-hailing-drivers",
            label: "Drivers",
            icon: UserCheck,
            path: "/ride-hailing/drivers",
            permission: "ride_hailing.view_drivers",
            subgroup: OPS,
          },
          {
            id: "ride-hailing-fleets",
            label: "Fleet Companies",
            icon: Building2,
            path: "/ride-hailing/fleets",
            permission: "ride_hailing.view_fleets",
            subgroup: OPS,
          },
          {
            id: "ride-hailing-rentals",
            label: "Self-Drive Rentals",
            icon: BookOpen,
            path: "/ride-hailing/rentals",
            permission: "ride_hailing.view_rentals",
            subgroup: OPS,
          },
          {
            id: "ride-hailing-safety",
            label: "Trust & Safety",
            icon: ShieldAlert,
            path: "/ride-hailing/safety",
            permission: "ride_hailing.view_safety",
            subgroup: OPS,
          },
          {
            id: "ride-hailing-catalog",
            label: "Catalog",
            icon: LayoutGrid,
            path: "/ride-hailing/catalog",
            permission: "ride_hailing.view_catalog",
            subgroup: SETUP,
          },
          {
            id: "ride-hailing-booking-types",
            label: "Booking Types",
            icon: ListPlus,
            path: "/ride-hailing/booking-types",
            permission: "ride_hailing.view_booking_types",
            subgroup: SETUP,
          },
          {
            id: "ride-hailing-fare",
            label: "Fare & Dispatch",
            icon: DollarSign,
            path: "/ride-hailing/fare",
            permission: "ride_hailing.view_fare",
            subgroup: SETUP,
          },
          {
            id: "ride-hailing-document-rules",
            label: "Document Rules",
            icon: FileText,
            path: "/ride-hailing/document-rules",
            permission: "ride_hailing.view_document_rules",
            subgroup: SETUP,
          },
          {
            id: "ride-hailing-onboarding",
            label: "Onboarding",
            icon: DoorOpen,
            path: "/ride-hailing/onboarding",
            permission: "ride_hailing.view_catalog",
            subgroup: SETUP,
          },
          {
            id: "ride-hailing-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/ride-hailing/customer-availability",
            permission: "ride_hailing.view_catalog",
            subgroup: SETUP,
          },
        ],
      },
      {
        id: "classified",
        label: "Classified (Buy/Sell)",
        icon: ShoppingBag,
        items: [
          {
            id: "classified-listing-moderation",
            label: "Listing Moderation",
            icon: ClipboardList,
            path: "/classified/moderation/listings",
            permission: "classified.view",
            subgroup: "Moderation",
          },
          {
            id: "classified-profile-moderation",
            label: "Vendor Moderation",
            icon: UserCheck,
            path: "/classified/moderation/profiles",
            permission: "classified.view",
            subgroup: "Moderation",
          },
          {
            id: "classified-regional-catalog",
            label: "Regional Catalog",
            icon: ShoppingBag,
            path: "/classified/regional-catalog",
            permission: "catalog.classified_catalog",
            subgroup: SETUP,
          },
          {
            id: "classified-contact-methods",
            label: "Contact Methods",
            icon: MessageCircle,
            path: "/classified/contact-methods",
            permission: "catalog.classified_catalog",
            subgroup: SETUP,
          },
          {
            id: "classified-onboarding",
            label: "Onboarding",
            icon: DoorOpen,
            path: "/classified/onboarding",
            permission: "catalog.classified_catalog",
            subgroup: SETUP,
          },
          {
            id: "classified-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/classified/customer-availability",
            permission: "catalog.classified_catalog",
            subgroup: SETUP,
          },
        ],
      },
      {
        // The job board: buyers post jobs, Professional Services vendors bid.
        // Consumer-only — nobody onboards as a "marketplace vendor", so there
        // is no Onboarding item here.
        id: "marketplace",
        label: "Marketplace",
        icon: Handshake,
        items: [
          {
            id: "marketplace-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/marketplace/customer-availability",
            permission: "marketplace.view",
          },
        ],
      },
      {
        // Third-party suppliers. ONE group, not three: Tudo eSIM, Tudo
        // PrePayment and Tudo Travel are customer-facing product names over
        // the same order pipeline, and an operator chasing an unresolved
        // purchase cares which order is stuck, not which brand it was sold
        // under. Product is a filter on the list, not a separate screen.
        id: "third-party",
        label: "Third-Party",
        icon: Plane,
        items: [
          {
            id: "third-party-orders",
            label: "Supplier Orders",
            icon: CreditCard,
            path: "/third-party/orders",
            permission: THIRD_PARTY.can("order", "view"),
          },
          // One availability screen per brand, because the three are stocked
          // independently - Oman has eSIM but no PrePayment coverage. Travel
          // keeps its three products on one screen: it is one brand with
          // three tabs, each with its own switch.
          {
            id: "third-party-esim-availability",
            label: "eSIM Availability",
            icon: Users,
            path: "/third-party/esim/availability",
            permission: THIRD_PARTY.can("order", "view"),
          },
          {
            id: "third-party-prepayment-availability",
            label: "Gift Cards Availability",
            icon: Users,
            path: "/third-party/prepayment/availability",
            permission: THIRD_PARTY.can("order", "view"),
          },
          {
            id: "third-party-travel-availability",
            label: "Travel Availability",
            icon: Users,
            path: "/third-party/travel/availability",
            permission: THIRD_PARTY.can("order", "view"),
          },
        ],
      },
      {
        // Tudo Social: the community/content umbrella (Tudopedia now;
        // TudoVerse, TudoTV and Games later). Consumer-only like Marketplace
        // and Inbox - nobody onboards as a "social vendor", so there is no
        // Onboarding item here.
        //
        // Tudo Chat is deliberately absent: it is the existing Inbox group,
        // and duplicating it here would imply a second chat product.
        id: "tudo-social",
        label: "Tudo Social",
        icon: BookOpen,
        items: [
          // --- Tudopedia: the editorial wiki -----------------------------
          {
            id: "tudo-social-articles",
            label: "Articles",
            icon: FileText,
            path: "/tudo-social/articles",
            permission: TUDO_SOCIAL.can("article", "view"),
            subgroup: PEDIA,
          },
          {
            id: "tudo-social-suggestions",
            label: "Reader Suggestions",
            icon: Lightbulb,
            path: "/tudo-social/suggestions",
            permission: TUDO_SOCIAL.can("suggestion", "view"),
            subgroup: PEDIA,
          },
          // Categories are ONE table discriminated by surface, so these three
          // entries open the SAME page on a different tab. They are listed per
          // product because that is where an admin looks for them.
          {
            id: "tudo-social-categories",
            label: "Categories",
            icon: Tag,
            path: "/tudo-social/categories",
            permission: TUDO_SOCIAL.can("space", "view"),
            subgroup: PEDIA,
          },
          // --- TudoVerse: the community feed -----------------------------
          // Named for what the page holds rather than "Moderation", which
          // gave no clue which product it moderated. Abuse reports live here
          // too: they can be raised on any surface, but this is the only
          // queue that shows them, so hiding them under a TudoTV heading
          // would be worse than the slight imprecision.
          {
            id: "tudo-social-moderation",
            label: "Posts & Reports",
            icon: ShieldAlert,
            path: "/tudo-social/moderation",
            permission: TUDO_SOCIAL.can("post", "view"),
            subgroup: VERSE,
          },
          // Rooms are admin-curated taxonomy — members pick one, they never
          // create one — so this is the only place they can come from. With
          // no rooms, the composer's "Choose a room" is empty.
          {
            id: "tudo-social-rooms",
            label: "Rooms",
            icon: Tag,
            path: "/tudo-social/rooms",
            permission: TUDO_SOCIAL.can("space", "view"),
            subgroup: VERSE,
          },
          // --- TudoTV: video ---------------------------------------------
          // Labels drop their "TudoTV" prefix: the subheading already says it,
          // and "TudoTV > TudoTV Channels" reads as a mistake.
          {
            id: "tudo-social-videos",
            label: "Videos",
            icon: Film,
            path: "/tudo-social/videos",
            permission: TUDO_SOCIAL.can("video", "view"),
            subgroup: TUDOTV,
          },
          {
            id: "tudo-social-channels",
            label: "Channels",
            icon: Clapperboard,
            path: "/tudo-social/channels",
            permission: TUDO_SOCIAL.can("channel", "view"),
            subgroup: TUDOTV,
          },
          {
            id: "tudo-social-tv-rules",
            label: "Required Documents",
            icon: FileCheck,
            path: "/tudo-social/tv-documents",
            permission: TUDO_SOCIAL.can("tv_document_rule", "view"),
            subgroup: TUDOTV,
          },
          // Who may open a channel in each country and city. A new channel is
          // refused where this is closed, whatever the documents say.
          {
            id: "tudo-social-onboarding",
            label: "Channel Onboarding",
            icon: DoorOpen,
            path: "/tudo-social/onboarding",
            permission: TUDO_SOCIAL.can("space_region", "view"),
            subgroup: TUDOTV,
          },
          // What a channel owner files a video under. With none, the
          // uploader's category dropdown is empty.
          {
            id: "tudo-social-tv-categories",
            label: "Categories",
            icon: Tag,
            path: "/tudo-social/tv-categories",
            permission: TUDO_SOCIAL.can("space", "view"),
            subgroup: TUDOTV,
          },
          // --- Tudo Games ------------------------------------------------
          {
            id: "tudo-social-games",
            label: "Catalogue & Prizes",
            icon: Dice5,
            path: "/tudo-social/games",
            permission: TUDO_SOCIAL.can("game", "view"),
            subgroup: GAMES,
          },
          {
            id: "tudo-social-game-plays",
            label: "Plays & Payouts",
            icon: Coins,
            path: "/tudo-social/game-plays",
            permission: TUDO_SOCIAL.can("game_play", "view"),
            subgroup: GAMES,
          },
          {
            id: "tudo-social-rewards",
            label: "Rewards",
            icon: Gift,
            path: "/tudo-social/rewards",
            permission: TUDO_SOCIAL.can("game_reward_rule", "view"),
            subgroup: GAMES,
          },
          // --- Shared across every surface --------------------------------
          // Availability gates the whole module, so it is the one item here
          // that belongs to no single product. The category lists used to sit
          // beside it; they now live under the product they configure,
          // because an admin hunting for TudoVerse rooms looks under
          // TudoVerse and nobody ever found them in Setup.
          {
            id: "tudo-social-customer-availability",
            label: "Customer Availability",
            icon: Users,
            path: "/tudo-social/availability",
            permission: TUDO_SOCIAL.can("space_region", "view"),
            subgroup: SETUP,
          },
        ],
      },
    ],
  },

  // ─── Platform setup (regions + global catalogs) ─────────────────────────
  {
    id: "platform-setup",
    label: "Platform Setup",
    groups: [
      {
        id: "regions",
        label: "Regions",
        icon: Map,
        items: [
          {
            id: "countries",
            label: "Countries",
            icon: Globe,
            path: "/countries",
            permission: "country.manage",
          },
          {
            id: "cities",
            label: "Cities",
            icon: MapPin,
            path: "/cities",
            permission: "country.manage",
          },
          {
            id: "landing",
            label: "Landing Page",
            icon: PanelsTopLeft,
            path: "/landing",
            permission: "country.manage",
          },
        ],
      },
      {
        // Platform-wide catalogs. Per-country / per-city rules live inside
        // each module's "Setup" subgroup.
        id: "global-catalog",
        label: "Global Catalog",
        icon: Layers,
        items: [
          {
            id: "catalog-global-modules",
            label: "Modules",
            icon: LayoutGrid,
            path: "/catalog/modules",
            permission: "country.manage",
          },
          {
            id: "catalog-service-catalog",
            label: "Services",
            icon: Briefcase,
            path: "/catalog/service-catalog",
            permission: "catalog.service_catalog",
          },
          {
            id: "catalog-restaurant-catalog",
            label: "Restaurants",
            icon: UtensilsCrossed,
            path: "/catalog/restaurant-catalog",
            permission: "catalog.restaurant_lookups",
          },
          {
            id: "classified-catalog",
            label: "Classified",
            icon: ShoppingBag,
            path: "/catalog/classified-catalog",
            permission: "catalog.classified_catalog",
          },
          {
            id: "companies-catalog",
            label: "Companies",
            icon: Building2,
            path: "/companies/catalog",
            permission: "country.manage",
          },
          {
            id: "catalog-documents",
            label: "Documents",
            icon: FileText,
            path: "/catalog/documents",
            permission: "catalog.documents",
          },
        ],
      },
    ],
  },

  // ─── Support ─────────────────────────────────────────────────────────────
  {
    id: "support",
    label: "Support",
    groups: [
      {
        id: "support",
        label: "Support Desk",
        icon: LifeBuoy,
        items: [
          {
            id: "support-dashboard",
            label: "Support Dashboard",
            icon: Headset,
            path: "/support",
            permission: "ticket.view",
            subgroup: "Tickets",
          },
          {
            // Customer-originated tickets only; admin-originated internal
            // tickets live under "Internal Tickets". Backend scopes per user.
            id: "ticket-queue",
            label: "User Tickets",
            icon: MessageSquare,
            path: "/support/tickets",
            permission: "ticket.view",
            subgroup: "Tickets",
          },
          {
            id: "my-tickets",
            label: "My Tickets",
            icon: UserCheck,
            path: "/support/my-tickets",
            permission: "ticket.view",
            subgroup: "Tickets",
          },
          {
            // Admin-originated tickets (admin->department / admin->franchise).
            // The "New internal ticket" CTA inside is separately gated by
            // ``support_ticket.create_internal``.
            id: "internal-tickets",
            label: "Internal Tickets",
            icon: Inbox,
            path: "/support/internal",
            permission: "ticket.view",
            subgroup: "Tickets",
          },
          {
            // Create / Edit / Delete inside are gated on ticket.manage_queues,
            // so read-only viewers get a list-only UI.
            id: "departments-list",
            label: "Departments",
            icon: LayoutGrid,
            path: "/departments",
            permission: "ticket.view",
            subgroup: "Teams",
          },
          {
            // Narrower than the generic ``ticket.manage`` so template
            // authoring can be delegated without SLA / routing access.
            id: "response-templates",
            label: "Response Templates",
            icon: FileText,
            path: "/support/templates",
            permission: "ticket.manage_templates",
            subgroup: "Knowledge",
          },
          {
            id: "help-center",
            label: "Help Center",
            icon: BookOpen,
            path: "/help-center",
            permission: "help_center.manage",
            subgroup: "Knowledge",
          },
          {
            // Narrower than ticket.view -- reviews are a support-admin /
            // IP-owner concern. The backend still accepts ticket.view.
            id: "platform-reviews",
            label: "Platform Reviews",
            icon: Star,
            path: "/support/platform-reviews",
            permission: "review.view",
            subgroup: "Feedback",
          },
        ],
      },
    ],
  },

  // ─── Business (money, growth, insights) ─────────────────────────────────
  {
    id: "business",
    label: "Business",
    groups: [
      {
        id: "finances",
        label: "Finance",
        icon: Wallet,
        items: [
          {
            id: "finance-overview",
            label: "Overview",
            icon: Wallet,
            path: "/finance",
            permission: "finance.view",
          },
          {
            id: "transactions",
            label: "Transactions",
            icon: ArrowLeftRight,
            path: "/finance/transactions",
            permission: "finance.view",
          },
          {
            id: "wallets",
            label: "Wallets",
            icon: CreditCard,
            path: "/finance/wallets",
            permission: "finance.view",
          },
          {
            id: "escrow",
            label: "Escrow",
            icon: Landmark,
            path: "/finance/escrow",
            permission: "finance.view",
          },
          {
            id: "payouts",
            label: "Payouts",
            icon: Receipt,
            path: "/finance/payouts",
            permission: "finance.view",
          },
        ],
      },
      {
        id: "affiliate-loyalty",
        label: "Growth & Rewards",
        icon: Coins,
        items: [
          {
            id: "affiliate-program",
            label: "Affiliate Program",
            icon: Gift,
            path: "/affiliate-program",
            permission: "affiliate.view",
          },
          {
            id: "loyalty-points",
            label: "Loyalty Points",
            icon: Star,
            path: "/loyalty-points",
            permission: "loyalty.view",
          },
        ],
      },
      {
        id: "analytics",
        label: "Analytics",
        icon: BarChart3,
        items: [
          {
            id: "analytics-realtime",
            label: "Real-time",
            icon: BarChart3,
            path: "/analytics",
            permission: "analytics.view",
            subgroup: "Insights",
          },
          {
            id: "user-acquisition",
            label: "User Acquisition",
            icon: TrendingUp,
            path: "/analytics/user-acquisition",
            permission: "analytics.view",
            subgroup: "Insights",
          },
          {
            id: "engagement",
            label: "Engagement",
            icon: Activity,
            path: "/analytics/engagement",
            permission: "analytics.view",
            subgroup: "Insights",
          },
          {
            id: "revenue-analytics",
            label: "Revenue",
            icon: PieChart,
            path: "/analytics/revenue",
            permission: "analytics.view",
            subgroup: "Insights",
          },
          {
            id: "conversion-funnels",
            label: "Conversion Funnels",
            icon: Gauge,
            path: "/analytics/conversion-funnels",
            permission: "analytics.view",
            subgroup: "Insights",
          },
          {
            id: "provider-performance",
            label: "Provider Performance",
            icon: UserCheck,
            path: "/analytics/provider-performance",
            permission: "analytics.view",
            subgroup: "Insights",
          },
          {
            id: "marketing-campaigns",
            label: "Marketing Campaigns",
            icon: Megaphone,
            path: "/analytics/marketing-campaigns",
            permission: "analytics.view",
            subgroup: "Insights",
          },
          {
            id: "system-health",
            label: "System Health",
            icon: ServerCog,
            path: "/analytics/system-health",
            permission: "analytics.view",
            subgroup: "Monitoring",
          },
          {
            id: "alerts",
            label: "Alerts",
            icon: ShieldAlert,
            path: "/analytics/alerts",
            permission: "analytics.view",
            subgroup: "Monitoring",
          },
          {
            id: "exports",
            label: "Data Exports",
            icon: FileDown,
            path: "/analytics/exports",
            permission: "analytics.view",
            subgroup: "Monitoring",
          },
        ],
      },
    ],
  },

  // ─── System ──────────────────────────────────────────────────────────────
  {
    id: "system",
    label: "System",
    groups: [
      {
        id: "access-control",
        label: "Access Control",
        icon: KeyRound,
        items: [
          {
            id: "rbac",
            label: "Roles & Permissions",
            icon: Shield,
            path: "/rbac",
            permission: "rbac.manage",
          },
          {
            // Narrower than rbac.manage so catalogue read-access can be
            // delegated on its own; rbac.manage holders also pass backend-side.
            id: "permissions-catalogue",
            label: "Permissions Catalogue",
            icon: ShieldAlert,
            path: "/rbac/permissions",
            permission: "permission.view",
          },
        ],
      },
      {
        id: "configuration",
        label: "Configuration",
        icon: Settings,
        items: [
          {
            id: "feature-flags",
            label: "Feature Flags",
            icon: ToggleRight,
            path: "/feature-flags",
            permission: "feature_flag.view",
          },
          {
            id: "notifications",
            label: "Notifications",
            icon: Bell,
            path: "/notifications",
            permission: "notification.view",
          },
          {
            id: "notification-campaigns",
            label: "Campaigns",
            icon: Megaphone,
            path: "/notification-campaigns",
            permission: "notification_campaign.view",
          },
          {
            // Messaging is shared by every vertical, so it sits here rather
            // than inside one module's group.
            id: "inbox-customer-availability",
            label: "Inbox Availability",
            icon: MessageSquare,
            path: "/inbox/customer-availability",
            permission: "inbox.manage",
          },
          {
            id: "ai-services",
            label: "AI Services",
            icon: Sparkles,
            path: "/ai-services",
            permission: "ai.manage",
          },
        ],
      },
      {
        id: "history",
        label: "Activity History",
        icon: History,
        items: [
          {
            id: "activity-history",
            label: "Activity History",
            icon: History,
            path: "/history",
            permission: "audit_log.view",
          },
        ],
      },
    ],
  },
];

/** Flat list of all groups. */
export const sidebarMenuGroups: SidebarMenuGroup[] = sidebarMenuSections.flatMap(
  (section) => section.groups,
);

/** Flat list of all menu items. */
export const sidebarMenuItems: SidebarMenuItem[] = sidebarMenuGroups.flatMap(
  (group) => group.items,
);
